<i18n src="./resources/locales.json"></i18n>
<i18n src="../../ecs-interaction-tabs/resources/locales.json"></i18n>
<template>
  <div class="email-container">
    <div class="inset-fade-effect" :dark="$vuetify.theme.dark" />
    <!-- Loading progress bar -->
    <div v-if="isLoading" class="loading-container">
      <div v-once>{{ $t('loading') }}</div>
      <div class="progress-bar">
        <v-progress-linear color="light-blue" indeterminate rounded height="6"></v-progress-linear>
      </div>
    </div>
    <div v-else-if="noElements" class="no-content">
      <h1 v-once>{{ $t('noElements.title') }}</h1>
      <p v-once>{{ $t('noElements.description') }}</p>
    </div>
    <!-- End loading progress bar  -->
    <div v-else class="surface custom-scrollbar email" :style="CSS_VARS">
      <EcsEmailReply
        v-if="isReplying"
        :email="email"
        :is-email-historic="isEmailHistoric"
        :active-element-id="activeElementId"
        :business-process-id="businessProcessId"
        @updateToEmails="onUpdateToEmails"
        @updateCcEmails="onUpdateCcEmails"
        @updateBccEmails="onUpdateBccEmails"
        @updateSubject="onSubjectUpdate"
        @quillChange="onQuillChange"
        @updateFromEmail="onUpdateFromEmail"
        @updateRequiredBccEmails="onUpdateRequiredBccEmails"
      />
      <EcsEmailContent
        v-if="showEmailContent"
        :data="email.outgoingEmailData"
        file-name="name"
        @onAttachmentDownload="onAttachmentDownload"
      />
      <EcsEmailContent
        v-if="email.isIncomingEmail"
        :data="email.incomingEmailData"
        @onAttachmentDownload="onAttachmentDownload"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsEmailReply from '@/components/ecs-email/ecs-email-reply/EcsEmailReply';
import EcsEmailContent from '@/components/ecs-email/ecs-email-content/EcsEmailContent';
import { ELEMENT_STATE } from '@/constants/element/constants';
import InteractionService from '@/services/interactionService';
import emailUtil from '@/utils/email';

export default {
  name: 'EcsEmail',

  components: {
    EcsEmailContent,
    EcsEmailReply
  },

  props: {
    interactionId: {
      type: String,
      default: ''
    }
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),
    ...mapGetters('ELEMENTS', ['getElementsForInteraction']),
    ...mapGetters('INTERACTIONS', ['getInteraction']),
    ...mapGetters('INTERACTIONS/emails', ['getEmailByInteractionId']),
    ...mapGetters('WINDOWS', ['getInteractionWindow']),
    email() {
      return this.getEmailByInteractionId(this.interactionId);
    },
    activeElementId() {
      const elements = this.getElementsForInteraction(this.interactionId);
      const activeElements = elements.filter(el => el.elementStateId !== ELEMENT_STATE.closed);
      return activeElements.length ? activeElements[0].elementId : '';
    },
    isEmailHistoric() {
      return this.getInteraction(this.interactionId).isHistoric;
    },
    businessProcessId() {
      return this.getInteraction(this.interactionId).routing.businessProcessId;
    },
    CSS_VARS() {
      const color = this.$vuetify.theme.dark ? '#fff' : '#000';
      return {
        '--ql-color': `${color}`
      };
    },
    isLoading() {
      return !this.email && !this.isEmailHistoric;
    },
    isReplying() {
      return !this.isEmailHistoric && this.email.isReplying;
    },
    showEmailContent() {
      return this.isEmailHistoric && !this.email.outgoingEmailData.canceled;
    },
    noElements() {
      return !this.email && this.isEmailHistoric;
    }
  },

  watch: {
    email(value, oldValue) {
      if (oldValue && !oldValue.isReplying && value.isReplying) {
        this.$el.scrollTop = 0;
      }
      if (value && value.outgoingEmailData && value.outgoingEmailData.signature) {
        this.handleEmailSignature(value);
      }
    }
  },

  methods: {
    ...mapActions('ELEMENTS', ['setElementContactInformationForEmail']),
    ...mapActions('INTERACTIONS', ['setInterationDestinationForEmail']),
    ...mapActions('INTERACTIONS/emails', [
      'updateSubject',
      'updateEmailResponseBody',
      'updateCcEmails',
      'updateBccEmails',
      'updateToEmails',
      'setSignatureCursorPosition',
      'updateRequiredBccEmails',
      'updateFromEmail'
    ]),
    onQuillChange({ content, delta, cursorIndex }) {
      const difference = this.processQuillDelta(delta);
      if (cursorIndex && cursorIndex.index - 1 < this.email.outgoingEmailData.signatureIndex) {
        const signatureIndex = this.email.outgoingEmailData.signatureIndex + difference;
        this.setSignatureCursorPosition({ interactionId: this.interactionId, signatureIndex });
      }
      this.updateEmailResponseBody({ interactionId: this.interactionId, content });
    },
    onUpdateToEmails(emails) {
      this.updateToEmails({ emails, interactionId: this.interactionId });
    },
    onUpdateCcEmails(emails) {
      this.updateCcEmails({ emails, interactionId: this.interactionId });
    },
    onUpdateBccEmails(emails) {
      this.updateBccEmails({ emails, interactionId: this.interactionId });
    },
    onSubjectUpdate(value) {
      this.updateSubject({ subject: value, interactionId: this.interactionId });
    },
    onUpdateFromEmail(value) {
      this.updateFromEmail({ fromEmail: value, interactionId: this.interactionId });
    },
    onUpdateRequiredBccEmails(emails) {
      this.updateRequiredBccEmails({ emails, interactionId: this.interactionId });
    },
    async onAttachmentDownload(file) {
      const response = await InteractionService.downloadEmailAttachments(this.activeElementId, [file.fileName]).catch(
        e => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('unableToDownload', { file: file.fileName }),
            type: 'error'
          });
        }
      );
      this.downloadFile(file, response.data[0]);
    },
    downloadFile(file, content) {
      const link = document.createElement('a');
      const url = `data:${file.contentType};base64,${content}`;
      link.href = url;
      link.setAttribute('download', file.fileName);
      document.body.appendChild(link);
      link.click();
    },
    handleEmailSignature(email) {
      if (email.outgoingEmailData && email.outgoingEmailData.body.length === 0) {
        this.setEmailSignatureInsideEmailBody(email);
      }
    },
    setEmailSignatureInsideEmailBody(email) {
      const signature = emailUtil.getEmailSignatureFormatted(
        email,
        email.outgoingEmailData.signature,
        this.loggedInAgent.name
      );
      this.updateEmailResponseBody({
        interactionId: email.interactionId,
        content: `<br>${signature}`
      });
      this.setSignatureCursorPosition({
        interactionId: email.interactionId,
        signatureIndex: 1
      });
    },
    processQuillDelta(delta) {
      let difference = 0;
      delta.ops.forEach((e, i) => {
        const key = Object.keys(e)[0];
        switch (key) {
          case 'insert':
            difference += delta.ops[i][key].length;
            break;
          case 'delete':
            difference -= delta.ops[i][key];
            break;
          default:
            break;
        }
      });
      return difference;
    }
  }
};
</script>

<style scoped>
.email {
  position: relative;
  width: 100%;
  height: 100%;
}
.email-container {
  width: 100%;
  height: 100%;
}
.email-custom-card {
  margin: 8px;
  background-color: var(--v-surface-lighten1);
  border-radius: 4px;
  padding: 0 !important;
  position: relative;
  z-index: 1;
}
.loading-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
}
.progress-bar {
  width: 60%;
}
.no-content {
  text-align: center;
  padding: 108px 0;
  background-color: var(--v-surface-base);
  opacity: 0.75;
}
</style>
